import { useLogto } from '@logto/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { changePasswordUrl, fetchMyAccount } from '../../api/account';
import { useActiveConversation } from '../../contexts/ActiveConversationContext';
import { useMobileDrawer } from '../../contexts/MobileDrawerContext';
import { type ThemeOverride, useTheme } from '../../contexts/ThemeContext';
import {
  IS_STAGING,
  LANG_FONTS,
  LANG_LABELS,
  type SupportedLang,
  SUPPORTED_LANGS,
} from '../../i18n';
import { ConversationList } from '../conversations/ConversationList';
import { IconChevronDown, IconClose, IconPlus } from '../primitives/Icons';
import { AccountMenu } from './AccountMenu';

const THEME_OPTIONS: Array<{ value: ThemeOverride; labelKey: string }> = [
  { value: 'auto', labelKey: 'themeAuto' },
  { value: 'morning', labelKey: 'themeMorning' },
  { value: 'afternoon', labelKey: 'themeAfternoon' },
  { value: 'evening', labelKey: 'themeEvening' },
];

export function LeftSidebar() {
  const { t, i18n } = useTranslation();
  const { override, setOverride, theme } = useTheme();
  const { runState } = useActiveConversation();
  const { isOpen, close, desktopCollapsed, isMobile } = useMobileDrawer();
  const { signOut, getIdTokenClaims, getAccessToken } = useLogto();
  const navigate = useNavigate();

  const [userName, setUserName] = useState<string>('');
  const [signingOut, setSigningOut] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Defaults to true so password users (the common case) get the active item
  // immediately; flipped to false only once we confirm a social-only account.
  const [hasPassword, setHasPassword] = useState(true);

  useEffect(() => {
    void getIdTokenClaims().then((c) => {
      setUserName(c?.name || c?.username || c?.email || 'User');
    });
  }, [getIdTokenClaims]);

  // Resolve whether this user has a password, to switch the account menu
  // between the active and disabled-social states. Uses the opaque OP token
  // (getAccessToken with no resource arg), required by the Logto Account API.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getAccessToken();
        const account = await fetchMyAccount(token);
        if (!cancelled && account && typeof account.hasPassword === 'boolean') {
          setHasPassword(account.hasPassword);
        }
      } catch {
        // Leave the default; the menu still works, just shows the active item.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessToken]);

  const isStreaming = runState === 'streaming' || runState === 'sending';

  const handleSignOut = () => {
    // Show the overlay immediately so the chat UI doesn't sit there
    // looking interactive while signOut prepares the redirect to Logto's
    // end-session endpoint. The overlay stays visible until the browser
    // navigates away; if signOut throws synchronously we clear it.
    setSigningOut(true);
    try {
      void signOut(`${window.location.origin}/logout`);
    } catch {
      setSigningOut(false);
    }
  };

  const handleChangePassword = () => {
    // Navigate to Logto's hosted change-password page, returning here after.
    // TODO(toast): on return, Logto appends ?show_success=password — surface a
    // success toast here once a toast component exists.
    window.location.href = changePasswordUrl(window.location.href);
  };

  const handleLangChange = (lang: SupportedLang) => {
    void i18n.changeLanguage(lang);
    document.body.style.setProperty('--ff', LANG_FONTS[lang]);
    document.documentElement.lang = lang;
  };

  const handleNewChat = () => {
    navigate('/c/new');
    close();
  };

  const className = [
    'sidebar-left',
    isOpen ? 'mobile-open' : '',
    desktopCollapsed ? 'collapsed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className={className}>
        <div className="sidebar-logo">
          <div className="logo-icon">
            <img src="/helloant_logo_cropped.png" alt="Hello Ant" />
          </div>
          <span className="logo-text">{t(IS_STAGING ? 'appNameStaging' : 'appName')}</span>
          <button
            type="button"
            className="sidebar-close-btn mobile-only"
            onClick={close}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>

        <button
          type="button"
          className="new-chat-btn"
          disabled={isStreaming}
          onClick={handleNewChat}
          title={t('newChat')}
        >
          <IconPlus />
          <span>{t('newChat')}</span>
        </button>

        <ConversationList />

        <div className="sidebar-bottom">
          <div className="theme-switcher">
            {THEME_OPTIONS.map((o) => (
              <button
                type="button"
                key={o.value}
                className={`theme-btn${override === o.value ? ' active' : ''}`}
                onClick={() => setOverride(o.value)}
                title={t(o.labelKey)}
              >
                {t(o.labelKey)}
              </button>
            ))}
          </div>

          <div className="lang-switcher">
            {SUPPORTED_LANGS.map((l) => (
              <button
                type="button"
                key={l}
                className={`lang-btn${i18n.resolvedLanguage === l ? ' active' : ''}`}
                onClick={() => handleLangChange(l)}
              >
                {LANG_LABELS[l]}
              </button>
            ))}
          </div>

          <div className="sidebar-user-zone">
            <div
              className={`sidebar-user-trigger${menuOpen ? ' open' : ''}`}
              onClick={() => setMenuOpen((v) => !v)}
              role="button"
              tabIndex={0}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setMenuOpen((v) => !v);
                }
              }}
            >
              <div className="user-avatar">{(userName[0] || 'U').toUpperCase()}</div>
              <div className="user-info">
                <div className="user-name" title={userName}>
                  {userName || 'User'}
                </div>
                <div className="user-role">{t('freePlan')}</div>
              </div>
              <span className={`trigger-chevron${menuOpen ? ' up' : ''}`}>
                <IconChevronDown />
              </span>
            </div>

            {menuOpen && (
              <AccountMenu
                isMobile={isMobile}
                hasPassword={hasPassword}
                onChangePassword={handleChangePassword}
                onSignOut={handleSignOut}
                onClose={() => setMenuOpen(false)}
              />
            )}
          </div>
        </div>
      </div>

      {signingOut && (
        <div className={`auth-screen signout-overlay theme-${theme}`}>
          <div className="loading-screen">{t('signingOut')}</div>
        </div>
      )}
    </>
  );
}
